import { useCallback, useMemo } from 'react'

import { isFunction, values } from 'lodash'
import { useSelector } from 'react-redux'

import { createQuota, deleteQuota, deleteQuotas, SerializationContexts, updateQuota, updateQuotas } from '../quota'
import { onError, onSuccess } from '../../apiHelper'
import { SerializationHeaders } from '../../http'
import { Quota } from '../../entities/quota'
import { Actions, toItemActionPairs } from '../../events'
import { getIdOrNull, isNonEmptyArray } from '../..'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'

const HEADERS = [new SerializationHeaders(SerializationContexts.LABELS)]

const useQuota = ({ callback }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const safeCallback = useMemo(() => isFunction(callback) ? callback : () => {}, [callback])

  const create = useCallback(quotas => {
    return createQuota(user, quotas, HEADERS).then(({ data }) => {
      safeCallback(data, Actions.CREATE)

      onSuccess(t('quota.create_bulk.success'))
    }).catch(error => {
      console.error(error)
      onError(t('quota.create_bulk.error'))
    })
  }, [user, t, safeCallback])

  const del = useCallback(quotas => {
    const isBulk = isNonEmptyArray(quotas)
    const config = isBulk ? { request: deleteQuotas, params: quotas.map(getIdOrNull) } : { request: deleteQuota, params: quotas }

    return config.request(user, config.params).then(({ data }) => {
      const items = isBulk ? values(data) : data
      safeCallback(...toItemActionPairs(items, Actions.DELETE))

      onSuccess(t('quota.delete.success'))
    }).catch(error => {
      console.error(error)
      onError(t('quota.delete.error'))
    })
  }, [t, user, safeCallback])

  const upsert = useCallback((quota, previousState) => {
    const config = quota.id === -1 ? { request: createQuota, action: Actions.CREATE } : { request: updateQuota, action: Actions.UPDATE }

    safeCallback(quota, config.action)

    return config.request(user, quota, HEADERS).then(response => {
      const newQuota = new Quota(response.data)

      if (config.action === Actions.CREATE) {
        safeCallback(quota, Actions.DELETE, newQuota, Actions.CREATE)
      } else {
        safeCallback(newQuota, Actions.UPDATE)
      }

      onSuccess(t(`quota.${config.action.toLowerCase()}.success`))
    }).catch(reason => {
      console.error(reason)

      config.action === Actions.CREATE ? safeCallback(quota, Actions.DELETE) : safeCallback(previousState, Actions.UPDATE)

      onError(t(`quota.${config.action.toLowerCase()}.error`))
    })
  }, [t, user, safeCallback])

  const update = useCallback((quotas, previousState) => {
    const isBulk = isNonEmptyArray(quotas)
    const request = isBulk ? updateQuotas : updateQuota

    return request(user, quotas, HEADERS).then(({ data }) => {
      const items = isBulk ? values(data) : data

      safeCallback(...toItemActionPairs(items, Actions.UPDATE))

      onSuccess(t('quota.update.success'))
    }).catch(reason => {
      console.error(reason)
      safeCallback(...toItemActionPairs(previousState, Actions.UPDATE))

      onError(t('quota.update.error'))
    })
  }, [t, user, safeCallback])

  const acceptDesiderata = useCallback(quota => {
    return updateQuota(user, new Quota({ ...quota, isDesiderata: false }), [new SerializationHeaders(SerializationContexts.LABELS)]).then(({ data }) => {
      safeCallback(data, Actions.UPDATE)

      onSuccess(t('quota.accept_desiderata.success'))
    }).catch(error => {
      console.error(error)

      onError(t('quota.accept_desiderata.error'))
    })
  }, [t, user, safeCallback])

  const refuseDesiderata = useCallback(quota => {
    return deleteQuota(user, quota).then(({ data }) => {
      safeCallback(data, Actions.DELETE)

      onSuccess(t('quota.refuse_desiderata.success'))
    }).catch(error => {
      console.error(error)
      onError(t('quota.refuse_desiderata.error'))
    })
  }, [t, user, safeCallback])

  return { create, delete: del, upsert, update, acceptDesiderata, refuseDesiderata }
}

export default useQuota
